import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { Spinner } from "@timeghost/ui/shadcn/spinner";
import { type JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ProjectReportPanel from "@/components/settings/projects/project-report-panel";
import stores from "@/lib/stores";
import { notifyTeamsAppLoaded, resolveAuthHost } from "@/lib/teams/host";
import { broadcastWorkspaceChange } from "@/lib/workspace/message-channel";
import { readCurrentWorkspaceId } from "@/lib/workspace/options";
import { useProject } from "@/queries/projects/useProjects.query";
import { useCurrentUser } from "@/queries/user/useCurrentUser.query";
import { dropWorkspaceScopedQueries } from "@/queries/workspace/useSwitchWorkspace.mutation";

const ProjectViewReportPage = (): JSX.Element => {
	const { id } = Route.useParams();
	const { workspaceid } = Route.useSearch();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { rawUser, isLoading: userLoading, refetch } = useCurrentUser();
	const [hostReady, setHostReady] = useState(false);
	const [hostIsTeams, setHostIsTeams] = useState(false);
	const [workspaceReady, setWorkspaceReady] = useState(!workspaceid);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			const host = await resolveAuthHost();
			if (cancelled) {
				return;
			}
			setHostIsTeams(host === "teams");
			setHostReady(true);
			if (host === "teams") {
				void notifyTeamsAppLoaded();
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!(workspaceid && rawUser) || userLoading) {
			return;
		}
		const currentId = readCurrentWorkspaceId(rawUser);
		if (currentId === workspaceid) {
			setWorkspaceReady(true);
			return;
		}

		let cancelled = false;
		void (async () => {
			const userId = typeof rawUser.id === "string" ? rawUser.id : null;
			if (!userId) {
				return;
			}
			localStorage.setItem("activeWorkspace", workspaceid);
			await stores.user.actions.update({
				...rawUser,
				id: userId,
				defaultWorkspace: workspaceid,
			});
			if (cancelled) {
				return;
			}
			broadcastWorkspaceChange(workspaceid);
			await dropWorkspaceScopedQueries(queryClient);
			await refetch();
			const url = new URL(window.location.href);
			url.searchParams.delete("workspaceid");
			window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
			setWorkspaceReady(true);
		})().catch(() => {
			if (!cancelled) {
				setWorkspaceReady(true);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [workspaceid, rawUser, userLoading, queryClient, refetch]);

	const projectQuery = useProject(id, {
		enabled: hostReady && workspaceReady && !!id,
	});

	if (!hostReady || (workspaceid && !workspaceReady) || userLoading) {
		return (
			<div className="flex min-h-dvh items-center justify-center bg-background">
				<Spinner size="default" />
			</div>
		);
	}

	if (!hostIsTeams) {
		return <Navigate to="/not-found" />;
	}

	if (projectQuery.isLoading) {
		return (
			<div className="flex min-h-dvh items-center justify-center bg-background">
				<Spinner size="default" />
			</div>
		);
	}

	if (projectQuery.isError || !projectQuery.data) {
		return (
			<div className="flex min-h-dvh items-center justify-center bg-background p-4">
				<p className="text-destructive text-sm">{t("teams.projectView.loadFailed")}</p>
			</div>
		);
	}

	return (
		<div className="min-h-dvh bg-background text-foreground">
			<header className="border-border border-b px-4 py-3">
				<h1 className="truncate font-semibold text-lg tracking-tight">{projectQuery.data.name}</h1>
				<p className="text-muted-foreground text-xs">{t("teams.projectView.reportOnly")}</p>
			</header>
			<main className="mx-auto w-full max-w-5xl p-4">
				<ProjectReportPanel project={projectQuery.data} />
			</main>
		</div>
	);
};

export const Route = createFileRoute("/settings/projectview/$id/report")({
	validateSearch: (
		search: Record<string, unknown>
	): {
		readonly workspaceid?: string;
		readonly in_teams?: string;
	} => ({
		workspaceid: typeof search.workspaceid === "string" ? search.workspaceid : undefined,
		in_teams: typeof search.in_teams === "string" ? search.in_teams : undefined,
	}),
	component: ProjectViewReportPage,
});
